/**
 * Android in-app · 开弹窗前选图策略
 *
 * 优先使用根布局 `PortfolioFilePickerHost` 的 mounted hidden input（与 Sender 一致），
 * 配合 `runPortfolioFilePickSession` 处理：
 * - 相册关闭后延迟交付 files
 * - visibility / blur / focus 取消检测（须在 click 之后 activate）
 *
 * Host 未就绪时（如单测）fallback 到 detached session，行为与 Host 内 pick 一致。
 */
import { clickHiddenFileInput } from "@/src/lib/bridge/prepareFileInput";
import { getBridge } from "@/src/lib/bridge/bridge";
import type { PortfolioUploadType } from "@components/PortfolioScreenshotModal/types";
import {
  resolvePortfolioFilePickCancelDetection,
  resolvePortfolioPickConfig,
} from "../shared/portfolioPickConfig";
import { resolvePortfolioInAppPlatformFlags } from "../shared/portfolioInAppPlatform";
import { runPortfolioFilePickSession } from "../shared/runPortfolioFilePickSession";
import { settlePickedPortfolioFiles } from "../shared/settlePickedPortfolioFiles";
import {
  getPortfolioFilePickerHost,
  waitForPortfolioFilePickerHost,
} from "../shared/portfolioFilePickerHostRegistry";

/** Host 不可用时的 detached fallback（测试 / 极端时序） */
async function pickPortfolioFilesAndroidDetached(
  uploadType: PortfolioUploadType,
): Promise<File[] | null> {
  if (typeof document === "undefined") {
    return null;
  }

  const bridge = getBridge();
  const platformFlags = resolvePortfolioInAppPlatformFlags();
  const { preset, accept, multiple, cancelPollMs } = resolvePortfolioPickConfig(
    uploadType,
    platformFlags,
  );

  const input = document.createElement("input");
  input.type = "file";
  input.accept = accept;
  input.multiple = multiple;
  input.style.display = "none";
  document.body.appendChild(input);

  const { promise, abort, activateCancelDetection } =
    runPortfolioFilePickSession({
      input,
      cancelPollMs,
      cancelDetection: resolvePortfolioFilePickCancelDetection(platformFlags),
      settleFiles: (picked) => settlePickedPortfolioFiles(uploadType, picked),
    });

  try {
    await clickHiddenFileInput(bridge, { current: input }, preset);
    activateCancelDetection();
    return await promise;
  } catch {
    abort();
    return null;
  } finally {
    input.remove();
  }
}

/**
 * Android 开弹窗前选图：等待 Host 注册（最多 3s）后委托 Host.pick。
 */
export async function pickPortfolioFilesOnAndroid(
  uploadType: PortfolioUploadType,
): Promise<File[] | null> {
  await waitForPortfolioFilePickerHost();
  const host = getPortfolioFilePickerHost();
  if (host) {
    return host.pick(uploadType);
  }
  return pickPortfolioFilesAndroidDetached(uploadType);
}
